import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from './ui/button';

const HeroSlider = () => {
  const { t, formatPrice, projects } = useApp();
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const carousel = (projects || []).filter((p) => p.inCarousel);
  const heroSlides = carousel.map((p) => ({
    id: p.id,
    image: p.image,
    developer: p.developer || '',
    title: p.name,
    subtitle: p.offer || p.description || '',
    priceAed: p.priceAed,
    plan: p.plan,
    projectId: p.id,
    eoi: p.eoi,
    eoiDate: p.eoiDate,
  }));
  const count = heroSlides.length;
  const cur = count ? index % count : 0;

  const go = useCallback((dir) => setIndex((i) => (i + dir + count) % count), [count]);

  useEffect(() => {
    if (!count) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 6500);
    return () => clearInterval(id);
  }, [count]);

  // preload only the next slide image (keeps initial load light, transitions smooth)
  useEffect(() => {
    if (count < 2) return;
    const img = new Image();
    img.src = heroSlides[(index + 1) % count].image;
  }, [index, count]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!count) return null;

  return (
    <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden bg-gobra-slate">
      <img key={heroSlides[cur].id} src={heroSlides[cur].image} alt={heroSlides[cur].title}
        loading="eager" fetchPriority="high" decoding="async"
        className="absolute inset-0 h-full w-full object-cover kenburns" />
      <div className="absolute inset-0 gobra-hero-overlay" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col justify-end pb-24 md:pb-28">
        <div key={cur} className="fade-up max-w-2xl">
          {heroSlides[cur].eoi && (
            <div>
              <span className="inline-flex items-center gap-2 bg-gobra-terracotta text-white text-xs md:text-sm font-semibold px-4 py-1.5 rounded-full mb-4 shadow-lg" data-testid="hero-eoi-badge">
                EOI OPEN{heroSlides[cur].eoiDate ? ` · ${new Date(heroSlides[cur].eoiDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              </span>
            </div>
          )}
          <span className="inline-block text-gobra-orange text-xs md:text-sm font-body font-semibold tracking-[0.25em] mb-3">
            {heroSlides[cur].developer.toUpperCase()}
          </span>
          <h1 className="font-title text-4xl md:text-6xl font-bold text-white leading-tight">
            {heroSlides[cur].title}
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-xl">
            {heroSlides[cur].subtitle}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-6 md:gap-10">
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider">{t('starting_from')}</div>
              <div className="font-title text-2xl md:text-3xl font-semibold text-white">{formatPrice(heroSlides[cur].priceAed)}</div>
            </div>
            <div className="h-10 w-px bg-white/25" />
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider">{t('payment_plan')}</div>
              <div className="font-title text-2xl md:text-3xl font-semibold text-white">{heroSlides[cur].plan} %</div>
            </div>
          </div>

          <Button onClick={() => navigate(`/project/${heroSlides[cur].projectId}`)}
            className="mt-7 bg-gobra-terracotta hover:bg-gobra-orange text-white rounded-full px-8 py-6 text-base">
            {t('discover_more')}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute z-20 bottom-8 right-4 md:right-8 flex items-center gap-3">
        <button onClick={() => go(-1)} aria-label="Previous"
          className="h-11 w-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-gobra-slate transition-colors backdrop-blur-sm">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={() => go(1)} aria-label="Next"
          className="h-11 w-11 rounded-full border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-gobra-slate transition-colors backdrop-blur-sm">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="absolute z-20 bottom-8 left-4 md:left-8 flex items-center gap-2">
        {heroSlides.map((s, i) => (
          <button key={s.id} onClick={() => setIndex(i)} aria-label={`Slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === cur ? 'w-8 bg-gobra-orange' : 'w-4 bg-white/50'}`} />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
